import { useNavigate } from 'react-router-dom';
import { LogOut, People, UserPlus } from '@/components/ui/icons';
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { cn, getInitials } from '@/lib/utils';

/**
 * Gmail-style account-switcher block (#297) for the account dropdown. Lists the
 * other stored (non-active) accounts, an "Add another account" action, and two
 * sign-out semantics — this account vs all accounts. Rendered inside the
 * existing account dropdowns in `Sidebar` and `AppMenuBar`, so it inherits their
 * `DropdownMenu` context.
 *
 * `itemClassName` lets the host (the dark AppMenuBar dropdown) restyle items to
 * match its surface; the Sidebar passes nothing and uses the default styling.
 */
export function AccountSwitcher({ itemClassName }: { itemClassName?: string }) {
  const navigate = useNavigate();
  const { user, accounts } = useAuth();

  const others = accounts.accounts.filter((a) => a.userId !== user?.id);

  return (
    <>
      {others.length > 0 && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="px-2 py-1 text-xs text-muted-foreground">
            Switch account
          </DropdownMenuLabel>
          <DropdownMenuGroup>
            {others.map((account) => (
              <DropdownMenuItem
                key={account.userId}
                className={cn('flex items-center gap-2', itemClassName)}
                onClick={() => {
                  void accounts.switchAccount(account.userId);
                }}
              >
                <Avatar className="h-6 w-6 shrink-0">
                  <AvatarFallback className="text-[10px]">
                    {getInitials(account.profile.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm">
                    {account.profile.name ||
                      account.email?.split('@')[0] ||
                      'Account'}
                  </span>
                  {account.email && (
                    <span className="truncate text-xs text-muted-foreground">
                      {account.email}
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </>
      )}

      <DropdownMenuSeparator />
      <DropdownMenuItem
        className={cn('flex items-center', itemClassName)}
        onClick={() => accounts.beginAddAccount()}
      >
        <UserPlus className="mr-2 h-4 w-4" />
        <span>Add another account</span>
      </DropdownMenuItem>

      <DropdownMenuSeparator />
      <DropdownMenuItem
        className={cn('flex items-center', itemClassName)}
        onClick={() => {
          if (!user) return;
          void accounts.signOutThisAccount(user.id).then(() => {
            // If no account remains, the session falls back to anon — send the
            // user to the sign-in page rather than leaving them on a gated view.
            if (accounts.accounts.length <= 1) navigate('/signin');
          });
        }}
      >
        <LogOut className="mr-2 h-4 w-4" />
        <span>Sign out of this account</span>
      </DropdownMenuItem>
      {accounts.accounts.length > 1 && (
        <DropdownMenuItem
          className={cn('flex items-center', itemClassName)}
          onClick={() => {
            void accounts.signOutAll().then(() => navigate('/signin'));
          }}
        >
          <People className="mr-2 h-4 w-4" />
          <span>Sign out of all accounts</span>
        </DropdownMenuItem>
      )}
    </>
  );
}
